import axios from 'axios';
import { API } from '@/App';
import { log } from '@/lib/logger';

const DISMISS_KEY = 'push_prompt_dismissed_at';
const DISMISS_DAYS = 30;

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'default';
}

export function wasRecentlyDismissed() {
  try {
    const iso = sessionStorage.getItem(DISMISS_KEY);
    if (!iso) return false;
    const ms = new Date(iso).getTime();
    if (Number.isNaN(ms)) return false;
    return Date.now() - ms < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    log.debug('push: sessionStorage read failed (private mode?)', e);
    return false;
  }
}

export function markDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch (e) {
    log.debug('push: sessionStorage write failed (private mode?)', e);
  }
}

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

let cachedRegistration = null;
export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  if (cachedRegistration) return cachedRegistration;
  cachedRegistration = await navigator.serviceWorker.register('/service-worker.js');
  return cachedRegistration;
}

/**
 * Full opt-in flow: register SW → request permission → subscribe with VAPID key → POST to backend.
 * Returns { ok: boolean, reason?: string }.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };

  try {
    const reg = await ensureServiceWorker();
    const keyRes = await axios.get(`${API}/push/public-key`);
    const applicationServerKey = urlB64ToUint8Array(keyRes.data.public_key);
    // Reuse existing subscription if any; otherwise create a fresh one.
    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    const raw = subscription.toJSON();
    await axios.post(`${API}/push/subscribe`, {
      endpoint: raw.endpoint,
      keys: raw.keys,
      expirationTime: raw.expirationTime ?? null,
    });
    return { ok: true };
  } catch (e) {
    log.error('subscribeToPush failed', e);
    return { ok: false, reason: 'error' };
  }
}

export async function unsubscribeFromPush() {
  try {
    const reg = await ensureServiceWorker();
    if (!reg) return { ok: false, reason: 'no-sw' };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try {
      await axios.post(`${API}/push/unsubscribe`, { endpoint });
    } catch (e) {
      // Best-effort cleanup — browser has already unsubscribed locally.
      log.debug('unsubscribeFromPush: server cleanup failed (already unsubscribed locally)', e);
    }
    return { ok: true };
  } catch (e) {
    log.error('unsubscribeFromPush failed', e);
    return { ok: false, reason: 'error' };
  }
}
